import { useEffect, useState } from "react";
import useApi from "../api/api";
import { useLocation } from "react-router-dom";
import { applyUi } from "./ui";
import { applyTabBranding } from "./tabBranding";
import { textOn } from "./contrast";

const FiBranding = () => {
  const api = useApi();
  const location = useLocation();

  const defaultBranding = {
    receiving_colors: {
      receiving_bg_primary: "#ffffff",
      receiving_bg_secondary: "#edf3fc",
      receiving_bg_tertiary: "#1d74ec",
      receiving_text: "#282f38",
    },
    receiving_logo: {
      logo_bg: "#ffffff",
      logo_height: "0px",
      logo_width: "0px",
      logo_vertical_margin: "10px",
    },
    receiving_logo_url: "",
    advertising: {
      advertising_description: undefined,
      advertising_link: undefined,
      advertising_button_text: undefined,
    },
    button_indexes: {
      paypal_index: 1,
      venmo_index: 2,
      p2c_index: 3,
      decline_index: 11,
      p2a_index: -1,
      prizeout_index: -1,
      prizeout_widget: false,
      coinbase_index: -1,
      // Google Pay payout (push to the card picked in the Google Pay sheet). -1 = off.
      google_pay_index: -1,
      // Apple Pay payout. -1 = off. Shown (but not usable) until the CDE has the Apple merchant.
      apple_pay_index: -1,
      // "Deposit to your <FI> card ending 1234" for recipients we already know. -1 = off.
      known_card_index: -1,
      // Visa Alias directories: one button per enabled directory, keyed by our internal
      // directory name, e.g. { cashapp: 5, venmo: 9 }. Empty object = no buttons.
      visa_alias: {},
    },
    prizeout_partner: {
      partner_id: "fb5f8b37-932d-4006-b58b-121afbe5b01d",
      environment: "sandbox",
    },
    visa_direct_enabled: false,
    mask_card_number: false,
    // Visual refresh feature flag (fi_branding.enable_receiving_ui_refresh).
    // Off by default: the pre-refresh UI renders until the FI is opted in.
    enable_receiving_ui_refresh: false,
    // Card entry iframe refresh flag (fi_branding.enable_cde_ui_refresh), passed
    // through to the CDE as the `ui_refresh` query param.
    enable_cde_ui_refresh: false,
    // Payee Management flag (fi_branding.enable_payee_management): one-tap claims with a saved
    // method, and the "Save for next time" / "Text me" choices after a claim. Off by default.
    enable_payee_management: false,
    // Browser tab favicon and title (fi_branding.app_favicon_url /
    // app_tab_title). Empty keeps the defaults from public/index.html.
    app_favicon_url: "",
    app_tab_title: "",
  };

  const [branding, setBranding] = useState(defaultBranding);
  // False until the branding request settles. Until then the defaults above
  // are placeholders, not this FI's answer, and must not pick the UI.
  const [loaded, setLoaded] = useState(false);
  const root = document.querySelector(":root");

  useEffect(() => {
    const transferId = location.search.split("?t=")[1].split("&i=")[0];

    const getFiBranding = async () => {
      try {
        const response = await api.get(`branding/transfer/ui/v2/${transferId}`);
        setBranding(response.data);
      } catch (error) {
        console.warn("get branding error", error);
      } finally {
        setLoaded(true);
      }
    };
    getFiBranding();
  }, [api, location]);

  const isColor = (str) => {
    if (/^#[0-9A-F]{6}$/i.test(str) === true) {
      return true;
    }
  };

  if (branding.receivingColors) {
    branding.receiving_colors= branding.receivingColors
    branding.receiving_logo= branding.receivingLogoStyling
    branding.receiving_logo_url= branding.receivingLogoUrl
    branding.button_indexes= branding.buttonIndexes
    branding.prizeout_partner= branding.prizeoutPartner
    branding.advertising = branding.advertisingInfo
    branding.visa_direct_enabled = branding.visaDirectEnabled !== undefined ? branding.visaDirectEnabled : false
    branding.mask_card_number = branding.maskCardNumber !== undefined ? branding.maskCardNumber : false
    branding.enable_receiving_ui_refresh = branding.enableReceivingUiRefresh === true
    branding.enable_cde_ui_refresh = branding.enableCdeUiRefresh === true
    branding.enable_payee_management = branding.enablePayeeManagement === true
    branding.app_favicon_url = branding.appFaviconUrl || ""
    branding.app_tab_title = branding.appTabTitle || ""
  }

  // Tab favicon and title, once the branding request settles.
  const faviconUrl = branding.app_favicon_url;
  const tabTitle = branding.app_tab_title;
  useEffect(() => {
    if (loaded) {
      applyTabBranding({ faviconUrl, title: tabTitle });
    }
  }, [loaded, faviconUrl, tabTitle]);

  // Select the UI generation's stylesheet. Only `true` opts in. Before the
  // response, keep what applyCachedUi set so a refresh FI doesn't flash legacy
  // (and so the cache isn't overwritten with "legacy" on every load).
  if (loaded) {
    applyUi(branding.enable_receiving_ui_refresh === true);
  }

  // check if all branding colors are valid hex codes and set css variables if so
  if (
    branding.receiving_colors &&
    isColor(branding.receiving_colors.receiving_bg_primary) &&
    isColor(branding.receiving_colors.receiving_bg_secondary) &&
    isColor(branding.receiving_colors.receiving_bg_tertiary) &&
    isColor(branding.receiving_colors.receiving_text)
  ) {
    root.style.setProperty(
      "--receiving_bg_primary",
      branding.receiving_colors.receiving_bg_primary
    );
    root.style.setProperty(
      "--receiving_bg_secondary",
      branding.receiving_colors.receiving_bg_secondary
    );
    root.style.setProperty(
      "--receiving_bg_tertiary",
      branding.receiving_colors.receiving_bg_tertiary
    );
    root.style.setProperty(
      "--receiving_text",
      branding.receiving_colors.receiving_text
    );
    // Refresh: the FI's brand colour is reserved for the primary action and
    // accent marks. In this app receiving_bg_tertiary is the FI's accent (it
    // has always been the branded button colour); receiving_bg_secondary is a
    // pale tint. The pressed shade uses relative colour syntax
    // (Safari 16.4+ / Chrome 119+).
    root.style.setProperty(
      "--fi-accent",
      branding.receiving_colors.receiving_bg_tertiary
    );
    root.style.setProperty(
      "--fi-accent-dark",
      `oklch(from ${branding.receiving_colors.receiving_bg_tertiary} calc(l * 0.86) c h)`
    );
  } else {
    setBranding({
      ...branding,
      receiving_colors: {
        ...defaultBranding.receiving_colors,
        // Optional and checked on its own below, so it survives a bad page colour.
        receiving_button_bg: branding.receiving_colors?.receiving_button_bg,
      },
    });
  }

  // Optional option-button background (receiving_colors.receiving_button_bg), independent of
  // the four colours above. Unset, no variables: the option buttons keep the page colours.
  const buttonBg = branding.receiving_colors?.receiving_button_bg;
  if (isColor(buttonBg)) {
    const buttonText = textOn(buttonBg);
    root.style.setProperty("--receiving_button_bg", buttonBg);
    root.style.setProperty("--receiving_button_text", buttonText);
    root.style.setProperty(
      "--receiving_button_hover",
      `color-mix(in srgb, ${buttonBg} 92%, ${buttonText})`
    );
  } else {
    root.style.removeProperty("--receiving_button_bg");
    root.style.removeProperty("--receiving_button_text");
    root.style.removeProperty("--receiving_button_hover");
  }

  if (branding.receiving_logo && isColor(branding.receiving_logo.logo_bg)) {
    root.style.setProperty("--logo_bg", branding.receiving_logo.logo_bg);
  }

  if (
    branding.button_indexes &&
    !(
      Number.isInteger(branding.button_indexes.paypal_index) ||
      Number.isInteger(branding.button_indexes.venmo_index) ||
      Number.isInteger(branding.button_indexes.p2c_index) ||
      Number.isInteger(branding.button_indexes.decline_index) ||
      Number.isInteger(branding.button_indexes.p2a_index) ||
      Number.isInteger(branding.button_indexes.prizeout_index) ||
      Number.isInteger(branding.button_indexes.coinbase_index) ||
      Number.isInteger(branding.button_indexes.google_pay_index) ||
      Number.isInteger(branding.button_indexes.apple_pay_index) ||
      typeof branding.button_indexes.visa_alias === "object"
    )
  ) {
    setBranding({
      ...branding,
      button_indexes: defaultBranding.button_indexes,
    });
  }

  if (
    branding.receiving_logo &&
    Math.abs(branding.receiving_logo.logo_height) >= 0 &&
    Math.abs(branding.receiving_logo.logo_width) >= 0 &&
    Math.abs(branding.receiving_logo.logo_vertical_margin) >= 0
  ) {
    root.style.setProperty(
      "--logo_height",
      `${branding.receiving_logo.logo_height}px`
    );
    root.style.setProperty(
      "--logo_width",
      `${branding.receiving_logo.logo_width}px`
    );
    root.style.setProperty(
      "--logo_vertical_margin",
      `${branding.receiving_logo.logo_vertical_margin}px`
    );
  }

  if (typeof branding.receiving_logo_url === "string") {
    root.style.setProperty(
      "--receiving_logo_url",
      `url(${branding?.receiving_logo_url})`
    );
  }

  return { branding, loaded };
};
export default FiBranding;
